/**
 * The on-page picture of a dropped file (spot tile, placement editor, mockup
 * tile). Print artwork can be 6000+ px a side; showing the original makes the
 * browser hold the full decoded image for every tile and repaint it on every
 * drag step. A file bigger than `PREVIEW_MAX_SIDE_PX` is drawn once onto a
 * canvas at that size and shown from the small copy instead; the placement
 * editor positions by percentages of the original's pixel size, so the copy
 * lines up exactly. The upload always sends the original file.
 *
 * Without `createImageBitmap` or a 2D canvas (old browsers, jsdom), or when
 * decoding fails, it falls back to the original file's object URL. The
 * caller owns the returned object URL and revokes it.
 */

/** Twice the widest the placement editor draws, for 2x screens. */
export const PREVIEW_MAX_SIDE_PX = 2048;

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    // WebP keeps transparency and is small; a browser that cannot encode it
    // returns a PNG instead.
    canvas.toBlob((blob) => resolve(blob), 'image/webp', 0.9);
  });
}

export async function createPreviewUrl(file: Blob, maxSide: number = PREVIEW_MAX_SIDE_PX): Promise<string> {
  const original = () => URL.createObjectURL(file);
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return original();
  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(file);
    const longest = Math.max(bitmap.width, bitmap.height);
    if (longest <= maxSide) return original();
    const scale = maxSide / longest;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext('2d');
    if (!context) return original();
    context.imageSmoothingQuality = 'high';
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await canvasToBlob(canvas);
    return blob ? URL.createObjectURL(blob) : original();
  } catch {
    return original();
  } finally {
    bitmap?.close();
  }
}
